import { useCallback, useMemo, type ChangeEvent, type ReactElement } from "react";
import { Text, useViewport } from "@primitives/index";
import { AdminSection, AdminSelect, Field, MutationError } from "../admin-ui";
import { useManagementGroups } from "../management-groups";
import type { QueueSummary } from "../queues";
import { useSetQueueOwner } from "../queues";

/**
 * Queue owner panel: re-home a shared queue under a management group.
 */

interface QueueOwnerPanelProps {
    readonly queue: QueueSummary;
    readonly canEdit: boolean;
}

export function QueueOwnerPanel({ queue, canEdit }: QueueOwnerPanelProps): ReactElement {
    const { isCompact } = useViewport();
    const groupsQuery = useManagementGroups(false);
    const setOwner = useSetQueueOwner();
    // Stable reference so the dependent useMemo below doesn't recompute on
    // every render of the parent.
    const groups = useMemo(() => groupsQuery.data?.items ?? [], [groupsQuery.data]);

    const currentOwner = useMemo(
        () =>
            queue.ownerManagementGroupId !== undefined && queue.ownerManagementGroupId !== null
                ? (groups.find((g) => g.id === queue.ownerManagementGroupId) ?? null)
                : null,
        [groups, queue.ownerManagementGroupId],
    );

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            const next = value.length === 0 ? null : value;
            // Avoid round-tripping when the selection didn't actually change
            // — keeps idle re-renders from stamping the same owner over again.
            const before = queue.ownerManagementGroupId ?? null;
            if (next === before) return;
            setOwner.mutate({ id: queue.id, managementGroupId: next });
        },
        [setOwner, queue.id, queue.ownerManagementGroupId],
    );

    return (
        <AdminSection
            title="Owner"
            description="Management group this queue rolls up to for reporting and bulk team operations."
        >
            <div style={{ maxWidth: isCompact ? "100%" : "40ch" }}>
                <Field label="Management group">
                    {(id) => (
                        <AdminSelect
                            id={id}
                            value={queue.ownerManagementGroupId ?? ""}
                            onChange={handleChange}
                            disabled={!canEdit || setOwner.isPending || groupsQuery.isLoading}
                            style={{ minWidth: isCompact ? "100%" : "26ch" }}
                        >
                            <option value="">(no owner)</option>
                            {groups.map((g) => (
                                <option key={g.id} value={g.id}>
                                    {g.name}
                                    {g.isSystem ? " · system" : ""}
                                </option>
                            ))}
                        </AdminSelect>
                    )}
                </Field>
            </div>
            {currentOwner === null &&
                queue.ownerManagementGroupId !== undefined &&
                queue.ownerManagementGroupId !== null && (
                    <Text role="caption" tone="muted">
                        Owner id {queue.ownerManagementGroupId} not found in active groups.
                    </Text>
                )}
            <MutationError error={setOwner.error} />
        </AdminSection>
    );
}
